import { $, esc, jsArg, footerHtml } from "../core/dom.js";
import { db, rpc, edgeFn } from "../core/supabaseClient.js";
import { state } from "../core/state.js";
import { fetchShows, fetchShow } from "../core/leagueShows.js";
import { fmtDate, clearTimersFor, showState } from "../core/format.js";
import { toast } from "../core/toast.js";
import { loadConfig, loadSongs } from "../core/session.js";
import { markTab } from "../core/layout.js";
import { settingsPanelHtml, wireSettingsPanel } from "./settings.js";
import { discordStatusLine } from "../core/discordHint.js"; // the Community editor's read-only Discord status (discord-routing.md, Part 2, the dev's call 7)
import { currentBracket, setSignupRows, signupBadgeLit } from "../core/switcher.js";
import { isNew } from "../core/signups.js";
import { TIEBREAK_LABELS } from "../core/tiebreak.js";
import { SLOT_LABELS, SLOT_TOOLTIPS, ONE_SET_EXCLUDED_TYPES, slotLabelFor } from "../core/slotTypes.js";
import { venueLocalInputValue, venueLocalToUTC, venueLongName, hasDstTransition } from "../core/venueTime.js";
import { RANKED_CHOICE_ENABLED, NEW_SIGNUP_DAYS, SIGNUP_SNOOZE_DAYS } from "../core/config.js";
import { slotDefs } from "./picks.js";

// Seasons only ever belong to a league's Official bracket, and the season
// editor has to keep working regardless of which bracket the switcher
// currently shows — an admin looking at Casual still needs to manage
// Official's seasons. So this resolves the league's Official bracket_id
// directly, rather than assuming state.currentBracketId.
function officialBracketId(){
  return state.leagues.find(l => l.league_id === state.currentLeagueId && l.bracket_kind === "official")?.bracket_id;
}

// Bracket-wide custom rules (brackets.config's custom_rules: string[]) — a
// soft cap enforced client-side by disabling the add button at the limit,
// not by rejecting a save that's already over it. `maxlength` on the input
// stops a typed-past-the-limit line at the source; readCustomRules() still
// re-slices defensively in case a value ever gets in past that (paste into
// a devtools-edited input, a future caller, etc.).
const CUSTOM_RULES_MAX = 10;
const CUSTOM_RULE_MAXLEN = 140;
function customRuleRow(text){
  return `<div class="admin-slot">
    <input class="rule-text" maxlength="${CUSTOM_RULE_MAXLEN}" value="${esc(text||"")}" placeholder="e.g. No repeating a cover pick within the same show" oninput="checkRuleCap()">
    <button class="btn ghost small" onclick="this.parentElement.remove(); checkRuleCap()">✕</button></div>`;
}
export function addCustomRule(){
  const box = $("#customrules");
  if (box.children.length >= CUSTOM_RULES_MAX) return;
  box.insertAdjacentHTML("beforeend", customRuleRow());
  checkRuleCap();
}
export function checkRuleCap(){
  const btn = $("#add-rule-btn");
  if (btn) btn.disabled = $("#customrules").children.length >= CUSTOM_RULES_MAX;
}
function readCustomRules(){
  return [...document.querySelectorAll("#customrules .rule-text")]
    .map(i => i.value.trim().slice(0, CUSTOM_RULE_MAXLEN))
    .filter(Boolean)
    .slice(0, CUSTOM_RULES_MAX);
}

// Per-device collapse state for the admin panel's sections — same idiom as
// ft_theme2/ft_bracket_id (core/theme.js, core/switcher.js): a plain
// localStorage-backed map read once at module load, written back on every
// toggle. Every section wired through collapsible() below defaults to
// collapsed (a missing key is falsy), so there's no separate "defaults"
// list to maintain — "Who's picked" and Settings never call collapsible()
// at all, since they're always expanded and have no toggle.
let sectionState = {};
try{ sectionState = JSON.parse(localStorage.getItem("ft_admin_sections") || "{}"); }catch(e){ sectionState = {}; }
function sectionOpen(key){ return !!sectionState[key]; }
export function toggleSection(key){
  sectionState[key] = !sectionOpen(key);
  localStorage.setItem("ft_admin_sections", JSON.stringify(sectionState));
  const body = $("#sec-"+key), btn = $("#sec-btn-"+key);
  if (body) body.classList.toggle("hidden", !sectionState[key]);
  if (btn) btn.textContent = sectionState[key] ? "hide" : "show";
}
// Collapsing only toggles a CSS class (.hidden => display:none) on the body
// div — the section's inputs/selects stay in the DOM with their values
// intact and fully readable by querySelectorAll (readSlots, saveConfig,
// etc.) whether or not the section is currently open. `alwaysVisible` is a
// slot for content that must stay visible even when collapsed (the
// Official-no-season warning) — it renders between the header and the
// collapsible body, never inside it.
function collapsible(key, title, bodyHtml, alwaysVisible = ""){
  const open = sectionOpen(key);
  return `<div class="panel">
    <div class="row"><h2 style="margin:0">${title}</h2>
      <button class="linkbtn" id="sec-btn-${key}" onclick="toggleSection('${key}')" style="margin-left:auto">${open ? "hide" : "show"}</button></div>
    ${alwaysVisible}
    <div id="sec-${key}" class="${open ? "" : "hidden"}">${bodyHtml}</div>
  </div>`;
}

// Session 4 step 5 — Global console, folded into the existing Admin screen
// as one more collapsible() section rather than a new nav tab: used a
// handful of times a year, Admin already uses this pattern everywhere, and
// a real tab would touch index.html's nav, layout.js's 3-column grid, and
// dom.js's $() redirect logic for no real benefit at this scale. Gated
// entirely on is_global_admin — league admins never see this section at all.
function globalConsoleHtml(){
  if (!state.session.is_global_admin) return "";
  return collapsible("global", "Global console", `
    <p class="muted">Global-only: create leagues, appoint league admins, and reset any player's PIN app-wide.</p>
    <div class="field"><label>New league name</label>
      <div class="row"><input id="gc-league-name" placeholder="e.g. Facebook League">
      <button class="btn ghost small" onclick="globalCreateLeague()">Create</button></div></div>
    <div class="err" id="gc-league-err"></div>
    <div class="field" style="margin-top:14px"><label>Appoint a league admin</label>
      <select id="gc-appoint-league">${(state.allLeagues||[]).map(l => `<option value="${l.id}">${esc(l.name)}</option>`).join("")}</select>
      <input id="gc-appoint-search" placeholder="Search player name…" oninput="globalSearchPlayers('appoint')" autocomplete="off"></div>
    <div id="gc-appoint-results"></div>
    <div class="field" style="margin-top:14px"><label>Reset a player's PIN (app-wide)</label>
      <input id="gc-reset-search" placeholder="Search player name…" oninput="globalSearchPlayers('reset')" autocomplete="off"></div>
    <div id="gc-reset-results"></div>
  `);
}
export async function loadGlobalLeagues(){
  // leagues has a public RLS read policy (same free read renderNoLeague()
  // already relies on) — no RPC needed just to list them.
  const { data, error } = await db.from("leagues").select("id,name").order("name");
  if (error){ toast(esc(error.message)); return; }
  state.allLeagues = data || [];
  const sel = $("#gc-appoint-league");
  if (sel) sel.innerHTML = state.allLeagues.map(l => `<option value="${l.id}">${esc(l.name)}</option>`).join("");
}
export async function globalCreateLeague(){
  $("#gc-league-err").textContent = "";
  const name = $("#gc-league-name").value.trim();
  if (!name){ $("#gc-league-err").textContent = "League needs a name"; return; }
  try{
    await rpc("global_create_league", { p_name:state.session.name, p_pin:state.session.pin, p_league_name:name });
    toast(`${esc(name)} created`, "score");
    $("#gc-league-name").value = "";
    loadGlobalLeagues();
  }catch(e){ $("#gc-league-err").textContent = e.message; }
}
// One search box pattern, reused for both the appoint-admin and reset-PIN
// player lookups — same shape as searchMembers()/addMember() above, just
// unscoped (global_find_players has no league_id, unlike admin_find_players).
export async function globalSearchPlayers(kind){
  const inputSel = kind === "appoint" ? "#gc-appoint-search" : "#gc-reset-search";
  const resultsSel = kind === "appoint" ? "#gc-appoint-results" : "#gc-reset-results";
  const q = $(inputSel).value.trim();
  if (q.length < 2){ $(resultsSel).innerHTML = ""; return; }
  try{
    const rows = await rpc("global_find_players", { p_name:state.session.name, p_pin:state.session.pin, p_query:q });
    $(resultsSel).innerHTML = (rows||[]).map(p => `
      <div class="pickres">
        <span class="nm" title="${esc(p.name)}">${esc(p.name)}</span>
        <span class="pt">${kind === "appoint"
          ? `<button class="btn ghost small" onclick="globalAppointAdmin('${p.player_id}', ${jsArg(p.name)})">Appoint</button>`
          : `<button class="btn ghost small" onclick="globalResetPin('${p.player_id}', ${jsArg(p.name)})">Reset PIN</button>`}</span>
      </div>`).join("") || '<p class="muted">No matches.</p>';
  }catch(e){ $(resultsSel).innerHTML = `<p class="err">${esc(e.message)}</p>`; }
}
export async function globalAppointAdmin(id, name){
  const leagueId = Number($("#gc-appoint-league").value);
  if (!leagueId){ toast("Pick a league first"); return; }
  try{
    await rpc("global_appoint_league_admin", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:leagueId, p_player_id:id });
    toast(`${esc(name)} appointed league admin`, "score");
    $("#gc-appoint-search").value = ""; $("#gc-appoint-results").innerHTML = "";
  }catch(e){ toast(esc(e.message)); }
}
// Same shape as resetMemberPin() above, but p_league_id:null — Global
// resetting someone app-wide has no "current league" to scope it to.
// admin_reset_player_pin treats a null league_id as "caller must be global",
// already enforced server-side, not re-checked here.
export async function globalResetPin(id, name){
  if (!confirm(`Reset ${name}'s PIN app-wide? They'll be forced to set a new one on next login. Relay the new PIN to them directly — it can't be recovered after this.`)) return;
  try{
    const r = await rpc("admin_reset_player_pin", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:null, p_player_id:id });
    alert(`New PIN for ${name}: ${r.new_pin}\n\nRelay this to them now — it will not be shown again.`);
  }catch(e){ toast(esc(e.message)); }
}

// Starting ladder offered when a bracket is switched to ranked mode without
// one. An empty editor with a lone "+ add rank" button reads as a broken
// feature, and an admin choosing ranked choice almost certainly wants a
// ladder — but note this is DISPLAY ONLY until they press Save. Nothing
// writes it to brackets.config on its own, so switching mode and navigating
// away persists nothing.
const DEFAULT_RANKED_LADDER = [5, 4, 3, 2, 1];

// Row position IS the rank, so the label is derived from position at render
// time rather than stored on the row — see renumberRanks() for why that
// matters after a deletion.
function rankRow(pts, i){
  return `<div class="admin-slot">
    <label style="min-width:4.2rem;opacity:.75">Rank ${i+1}</label>
    <input class="rank-pts" type="number" min="0" value="${pts ?? ""}">
    <button class="btn ghost small" onclick="this.parentElement.remove(); renumberRanks()">✕</button></div>`;
}
export function addRankRow(){
  const box = $("#rankladder");
  box.insertAdjacentHTML("beforeend", rankRow("", box.children.length));
}
// Deleting a middle row would otherwise leave labels reading "Rank 1, Rank 3,
// Rank 4" while position — the thing that actually determines the rank, both
// here and in readLadder() — says otherwise. Relabel from position so the
// visible label can't disagree with the real ordering.
export function renumberRanks(){
  [...document.querySelectorAll("#rankladder .admin-slot")].forEach((row, i) => {
    row.querySelector("label").textContent = `Rank ${i+1}`;
  });
}

// The mode-dependent slice of the admin panel, extracted so onModeChange()
// can re-render just this region when the scoring-mode select changes,
// without disturbing Master switch / Seasons / House rules (a full
// renderAdmin() would discard unsaved edits in those).
//
// `mode` is a parameter rather than read from state.cfg on purpose:
// state.cfg must stay a faithful copy of what's in the database, because
// saveConfig()'s fallbacks read it to preserve fields whose inputs aren't
// currently rendered. A locally-mutated state.cfg would corrupt that, and
// would also let a mode switch leak into the pick sheet before it's saved.
function rulesRegionHtml(cfg, mode){
  const b = cfg.bonuses || {};
  if (mode === "ranked_choice") {
    const stored = cfg.ranked?.ladder ?? [];
    const ladder = stored.length ? stored : DEFAULT_RANKED_LADDER;
    // Unsaved-default flag (admin-panel.md → "EMPTY RANKED LADDER",
    // decision 3): the editor below previews the default when nothing is
    // stored, and on 2026-10-26 that preview was mistaken for a saved
    // ladder — ranked mode shipped with no rows. Say so, in the panel.
    const unsaved = !stored.length
      ? `<div class="noticebox" id="ladder-unsaved">⚠️ Default ladder shown — <b>not saved yet</b>. Until Save rules (or Save master switch) stores it, the pick sheet for this bracket has no rows.</div>`
      : "";
    return collapsible("rules-ranked", "Game rules — ranked choice", `
      ${unsaved}
      <p class="muted">Players pick one song per rank. A pick scores its rank's value if that
        song is played anywhere in the show — position doesn't matter, so a single ladder
        covers every show regardless of format. There's no separate one-set section here.</p>
      <div id="rankladder">${ladder.map(rankRow).join("")}</div>
      <button class="btn ghost small" onclick="addRankRow()">+ add rank</button>
      <p class="muted" style="margin-top:6px">Row order is the rank — the first row is Rank 1.
        Every rank needs a value — use ✕ to remove a rank. Cover, debut, and "Any Debut"
        don't apply in this mode; perfect sheet still does and lives under Master switch.</p>
    `);
  }
  const os = cfg.oneset || { slots:[
      {key:"opener",type:"opener",label:"Opener",points:2},
      {key:"closer",type:"closer",label:"Closer",points:2},
      {key:"cover1",type:"cover_pick",label:"Cover Pick",points:2}
    ], flat_picks:3, flat_points:1 };
  return `
    ${collapsible("rules-standard", "Game rules — standard shows", `
      <p class="muted">Slotted picks (position matters):</p>
      <div id="slots">${(cfg.slots||[]).map(sl => adminSlotRow(sl, "standard")).join("")}</div>
      <button class="btn ghost small" onclick="addSlot('slots')">+ add slot</button>
      <div class="grid2" style="margin-top:14px">
        <div class="field"><label>Flat picks (count)</label><input id="c-flat" type="number" min="0" value="${cfg.flat_picks}"></div>
        <div class="field"><label>Points per flat pick</label><input id="c-flatpts" type="number" min="0" value="${cfg.flat_points}"></div>
        <div class="field"><label>Partial credit (slot song played elsewhere)</label>
          <select id="c-partial"><option value="true" ${cfg.partial_credit?"selected":""}>On</option><option value="false" ${!cfg.partial_credit?"selected":""}>Off</option></select></div>
        <div class="field"><label>Partial points</label><input id="c-partpts" type="number" min="0" value="${cfg.partial_points}"></div>
        <div class="field"><label>Bonus: cover</label><input id="c-bcover" type="number" min="0" value="${b.cover||0}"></div>
        <div class="field"><label>Bonus: debut</label><input id="c-bdebut" type="number" min="0" value="${b.debut||0}"></div>
        <div class="field"><label>Allow duplicate songs across picks</label>
          <select id="c-dupes"><option value="false" ${!cfg.allow_duplicates?"selected":""}>No</option><option value="true" ${cfg.allow_duplicates?"selected":""}>Yes</option></select></div>
        <div class="field"><label>Wildcard: "Any Debut" (hits if any debut is played)</label>
          <select id="c-wcdebut"><option value="true" ${(cfg.wildcards?.debut ?? true)?"selected":""}>Players may pick it</option><option value="false" ${(cfg.wildcards?.debut ?? true)?"":"selected"}>Off</option></select></div>
      </div>
    `)}
    ${collapsible("rules-oneset", "Game rules — one-set shows", `
      <p class="muted">Used for shows toggled to "1 set" below. Festival-tagged shows sync in as 1 set automatically.</p>
      <div id="slots1">${(os.slots||[]).map(sl => adminSlotRow(sl, "one_set")).join("")}</div>
      <button class="btn ghost small" onclick="addSlot('slots1')">+ add slot</button>
      <div class="grid2" style="margin-top:14px">
        <div class="field"><label>Flat picks (count)</label><input id="c1-flat" type="number" min="0" value="${os.flat_picks}"></div>
        <div class="field"><label>Points per flat pick</label><input id="c1-flatpts" type="number" min="0" value="${os.flat_points}"></div>
      </div>
    `)}`;
}

// Re-render only the mode-dependent region when the scoring-mode select
// changes. Reads the new mode from the select; does not touch state.cfg.
export function onModeChange(){
  $("#rules-region").innerHTML = rulesRegionHtml(state.cfg, $("#c-mode").value);
}

export async function renderAdmin(){
  clearTimersFor("admin"); state.tab = "admin"; markTab();
  rosterFilter = "needs"; // chase list opens on Needs picks every load (admin-panel.md → Who's Picked)
  await loadConfig();
  const cfg = state.cfg;
  const b = cfg.bonuses || {};
  // A config with no `mode` key is slot mode — that's every bracket that
  // existed before ranked choice, so absence must keep working untouched.
  const mode = cfg.mode || "slots";
  // The ranked option is withheld until the scorer is deployed (see
  // RANKED_CHOICE_ENABLED), but it's still offered when this bracket is
  // ALREADY ranked — otherwise the select couldn't represent the state it
  // just loaded, would display "Slots", and the next save would silently
  // rewrite the bracket's real mode.
  const showRanked = RANKED_CHOICE_ENABLED || mode === "ranked_choice";
  const [shows, seasonsA] = await Promise.all([
    fetchShows(q => q.gte("showdate", new Date(Date.now()-7*864e5).toISOString().slice(0,10)).order("showdate")),
    rpc("get_bracket_seasons", { p_bracket_id: officialBracketId() }),
  ]);
  const todayA = new Date().toLocaleDateString('sv');
  const nextShow = (shows||[]).find(sh => sh.showdate >= todayA) || (shows||[])[(shows||[]).length-1];
  // Official-no-season warning: checked against the show's own date (an
  // upcoming show, not "today"), independent of which bracket the switcher
  // currently shows — Seasons always manages the league's Official bracket
  // regardless (see officialBracketId above). Rendered via collapsible()'s
  // alwaysVisible slot, outside the Seasons section's collapsible body, so
  // it stays visible whether or not that section is expanded.
  const uncoveredShows = (shows||[]).filter(sh => sh.showdate >= todayA
    && !(seasonsA||[]).some(se => se.start_date <= sh.showdate && sh.showdate <= se.end_date));
  const seasonWarning = uncoveredShows.length ? `<div class="noticebox">
      ⚠️ Official has no season covering ${uncoveredShows.length === 1 ? "an upcoming show" : uncoveredShows.length + " upcoming shows"} —
      picks will be blocked there until a season is added: ${uncoveredShows.map(sh => {
        // Just the compact M/D, no venue — this list has no cap and grows one
        // entry per un-seasoned upcoming show, so venue names get unwieldy
        // fast and the date alone is enough to act on. Sliced straight off
        // the "YYYY-MM-DD" string rather than through a Date object, so
        // there's no local-timezone rollover risk right at a date boundary.
        const [, m, d] = sh.showdate.split("-");
        return `${Number(m)}/${Number(d)}`;
      }).join(", ")}</div>` : "";
  // ---- SECTION ORDER IS GROUPED BY SCOPE. Don't reorder casually. ----
  // League-scoped first, then a scope line, then everything scoped to the
  // CURRENT bracket, then global, then personal. The line is only truthful
  // if nothing league-scoped sits under it, so moving a section across it
  // is a behavioural change, not a cosmetic one.
  //
  // Seasons is the subtle one, and its placement is deliberate: it sits
  // just ABOVE the line, next to but NOT with Season tiebreakers. Despite
  // the matching names the two have different scopes — Seasons resolves
  // officialBracketId() and edits Official no matter which bracket the
  // switcher shows, while Season tiebreakers is current-bracket config
  // (cfg.tiebreakers, saved with p_bracket_id: state.currentBracketId)
  // that only renders when the current bracket IS Official. Putting
  // Seasons below the line would make the line actively false for it.
  // This looks wrong when scanning by name and is correct by behaviour —
  // please don't "fix" it by moving them together.
  // Three dividers that BOUND EACH OTHER — each governs until the next. That
  // is what makes their claims true: the old single line said "everything
  // below applies to this bracket only", which was false for Manual sync &
  // scoring, Global console and Settings, all of which sit below it.
  //
  // Note the copy carries its own bound too: "The RULES below" names the
  // subject, so it stays true even if a reader misses the next divider. A
  // divider that says "everything below" is fragile by construction.
  const scopeLine = currentBracket()
    ? `<div class="scopeline">
        <div class="scopeline-label">Editing <b>${esc(currentBracket().bracket_name)}</b> · ${esc(currentBracket().league_name)}</div>
        <div class="scopeline-note">The rules below apply to this bracket only.</div>
      </div>`
    : "";
  // Gated on is_global_admin to match the two sections it heads — otherwise a
  // league admin gets a divider over nothing.
  const adminLine = `<div class="scopeline">
      <div class="scopeline-label">Administrator</div>
      <div class="scopeline-note">These affect every league, not just this one.</div>
    </div>`;
  // No note: "Your account" is self-evident, and Settings is visually
  // distinct anyway. Its job is to stop the Administrator claim above from
  // appearing to cover Settings.
  const accountLine = `<div class="scopeline"><div class="scopeline-label">Your account</div></div>`;
  // Who's picked is the highest-frequency section, so it sits at the TOP —
  // above the scope line rather than under it. That costs it the line's
  // scope claim, which matters more here than almost anywhere: pick status
  // read for the wrong bracket is a wrong conclusion with nothing to correct
  // it. Hence its own badge, from the SAME source the scope line uses, so
  // the two can never disagree.
  //
  // Kept as its own element rather than concatenated into the heading string,
  // even though it renders as one phrase — "Who's picked: Casual". The
  // separation is structural (addressable, separately styleable if that is
  // ever wanted); the styling deliberately does NOT advertise it.
  //
  // It sits INSIDE the <h2>, not beside it, for a specific reason: a sibling
  // inherits from .row, so matching the heading would mean re-declaring
  // Fraunces, the variation settings and 1.25rem — duplicating styles.css:44
  // and :101, which then drift the day either changes. As a child it inherits
  // all of that for free, and .panel h2's overflow-wrap:anywhere applies to
  // the whole phrase, so a long bracket name wraps with the heading instead
  // of overflowing the ~224px desktop admin column.
  //
  // Colon, not a dash or the scope line's "·": the relationship is
  // heading-to-qualifier, not two peers. "·" specifically is doing the
  // peer job in scopeLine above (bracket · league), so reusing it here would
  // imply the same relationship and mislead.
  //
  // Were a collapsible() section ever to need the same, do NOT make its
  // `title` accept markup — it is a plain string at all 11 call sites, so
  // escaping is nobody's problem today; making it HTML invites an unescaped
  // interpolation into a function that currently cannot have that bug. Fold
  // `alwaysVisible` and a qualifier into a trailing options object instead.
  const bracketBadge = currentBracket()
    ? `<span class="section-scope">: ${esc(currentBracket().bracket_name)}</span>`
    : "";
  $("#main").innerHTML = `
    <div class="panel" id="whospicked">
      <h2>Who's picked${bracketBadge}</h2>
      <div class="field"><label>Show</label>
        <select id="roster-show" onchange="loadRoster()">
          ${(shows||[]).map(sh => `<option value="${sh.id}" ${nextShow && sh.id===nextShow.id ? "selected" : ""}>${fmtDate(sh.showdate)} — ${esc(sh.venue||"TBA")}</option>`).join("")}
        </select></div>
      <div id="roster"><p class="muted">Pick a show.</p></div>
    </div>
    ${collapsible("shows", "Shows: cutoffs &amp; finalizing", `
      <p class="muted">Cutoffs are shown and edited in each show's venue-local time. New shows default to 6 PM venue-local.</p>
      ${(shows||[]).map(sh => {
        const tz = sh.timezone || null;
        const inputVal = !sh.cutoff_at ? ""
          : tz ? venueLocalInputValue(sh.cutoff_at, tz)
          : new Date(new Date(sh.cutoff_at).getTime()-new Date().getTimezoneOffset()*6e4).toISOString().slice(0,16);
        // The input already shows the time — this only needs to name the
        // zone (and carry the caveats that add real information), not
        // restate the value a second time.
        const zoneLabel = !sh.cutoff_at ? "" : tz
          ? `<div class="muted" style="font-size:.75rem;margin:8px 0 2px">Timezone: <b style="color:var(--cream)">${esc(venueLongName(sh.cutoff_at, tz))}</b>${
              hasDstTransition(inputVal, tz) ? ' <span style="color:var(--coral)">⚠ DST changes on this date — double-check this time</span>' : ""
            }</div>`
          : `<div class="muted" style="font-size:.75rem;margin:8px 0 2px"><span style="color:var(--coral)">Device time — venue timezone unknown</span></div>`;
        // Show-sync flag (docs/show_sync_plan.md): written by the daily sync
        // when it refused a change to a finalized show, saw a show vanish
        // from The Carton, or left an admin-set cutoff alone on a date move.
        // Rendered under the row head so it reads as about THIS show; Clear
        // goes through admin_clear_sync_flag (allow-plus-log) and re-renders.
        const flagNotice = sh.sync_flag
          ? `<div class="noticebox sync-flag" data-show="${sh.id}">⚠️ Show sync: ${esc(sh.sync_flag)} <button class="linkbtn" onclick="clearSyncFlag(${sh.id}, this)">Clear</button></div>`
          : "";
        return `<div class="arow">
        <div class="arow-head"><span class="date">${fmtDate(sh.showdate)}</span><span class="venue">${esc(sh.venue||"TBA")}</span></div>
        ${flagNotice}
        ${zoneLabel}
        <input class="cutoff-in" type="datetime-local" step="900" data-show="${sh.id}" data-tz="${tz||''}" value="${inputVal}">
        <div class="switcher" style="margin-bottom:8px" title="pick sheet format">
          <button class="linkbtn switcher-btn${sh.format!=='one_set'?" on":""}" onclick="toggleFormat(${sh.id}, 'standard')">2 set</button>
          <button class="linkbtn switcher-btn${sh.format==='one_set'?" on":""}" onclick="toggleFormat(${sh.id}, 'one_set')">1 set</button>
        </div>
        <div class="arow-btns">
          <button onclick="saveCutoff(${sh.id}, this)">Change cutoff</button>
          ${sh.status!=='final' && sh.cutoff_at && new Date(sh.cutoff_at) < new Date() ? '<button onclick="finalizeShow('+sh.id+', this)" style="border-color:var(--coral);color:var(--coral)">Finalize</button>' : ''}
          ${sh.status==='final' ? '<button onclick="reopenShow('+sh.id+', this)" style="border-color:var(--coral);color:var(--coral)">Reopen</button>' : ''}
        </div>
      </div>`;
      }).join("") || '<p class="muted">No shows — sync first.</p>'}
    `)}
    ${collapsible("members", "Members", `
      <div class="field"><label>Add a member</label>
        <input id="member-search" placeholder="Search registered players by name…" oninput="searchMembers()" autocomplete="off"></div>
      <div id="member-results"></div>
      <div id="playerlist"><p class="muted">Loading…</p></div>
      <div id="unaffiliated"></div>
      <button class="linkbtn" id="banToggle" onclick="toggleBans()" style="margin-top:8px">show ban list</button>
      <div id="banlist" class="hidden" style="margin-top:6px"></div>
    `)}
    ${collapsible("community", "Community", `
      <p class="muted">Shown to players on the "not in a league yet" screen. These apply to the whole league, both brackets.
        Anyone with the app can read them, so use a Discord invite you're willing to rotate.</p>
      <div class="field"><label>Facebook group URL</label>
        <input id="lc-fb" type="url" placeholder="https://…" value="${esc(currentBracket()?.fb_url || "")}" autocomplete="off"></div>
      <div class="field"><label>Discord invite URL</label>
        <input id="lc-discord" type="url" placeholder="https://…" value="${esc(currentBracket()?.discord_url || "")}" autocomplete="off"></div>
      <p class="muted" id="lc-discord-status">${esc(discordStatusLine(currentBracket()))}</p>
      <button class="btn ghost small" onclick="saveLeagueConfig()">Save links</button>
      <div class="err" id="lc-err"></div>
    `)}
    ${collapsible("seasons", "Seasons (Official)", `
      <p class="muted">Named date ranges — shows sort themselves in by date.</p>
      <div id="seasonrows">${(seasonsA||[]).map(seasonRow).join("")}</div>
      <button class="btn ghost small" onclick="addSeasonRow()">+ add season</button>
    `, seasonWarning)}
    ${scopeLine}
    ${collapsible("master", "Voting &amp; scoring mode", `
      <div class="field"><label>Voting override</label>
        <select id="c-override">
          <option value="auto" ${(cfg.voting_override||"auto")==="auto"?"selected":""}>Auto — cutoffs decide</option>
          <option value="locked" ${cfg.voting_override==="locked"?"selected":""}>Locked — nobody can vote</option>
          <option value="open" ${cfg.voting_override==="open"?"selected":""}>Open — voting open for today + future shows</option>
        </select></div>
      <div class="field"><label>Scoring mode</label>
        <select id="c-mode" onchange="onModeChange()">
          <option value="slots" ${mode!=="ranked_choice"?"selected":""}>Slots — position matters (opener, closers, encore)</option>
          ${showRanked ? `<option value="ranked_choice" ${mode==="ranked_choice"?"selected":""}>Ranked choice — N picks against a fixed ladder</option>` : ""}
        </select></div>
      <div class="field"><label>Bonus: perfect sheet (every pick hits)</label><input id="c-bperfect" type="number" min="0" value="${b.perfect||0}"></div>
      <button class="btn" onclick="saveMasterSwitch()">Save master switch</button>
      <div class="err" id="ms-err"></div>
      <p class="muted" style="margin-top:6px">Saves these three fields only — the rules
        sections below have their own save. Enforced in the database; Auto is normal
        operation. Perfect sheet lives here rather than with the other bonuses because
        it's the one bonus that applies in every scoring mode — it scores the whole
        sheet being right, not any individual song.</p>
    `)}
    <div id="rules-region">${rulesRegionHtml(cfg, mode)}</div>
    ${collapsible("rules-custom", "House rules", `
      <p class="muted">Bracket-wide house rules, shown on the pick sheet's "The Rules"
        card below the auto-generated slot definitions. Casual and Official can each
        have their own. Up to ${CUSTOM_RULES_MAX} rules, ${CUSTOM_RULE_MAXLEN}
        characters each.</p>
      <div id="customrules">${(cfg.custom_rules||[]).map(customRuleRow).join("")}</div>
      <button class="btn ghost small" id="add-rule-btn" onclick="addCustomRule()"
        ${(cfg.custom_rules||[]).length >= CUSTOM_RULES_MAX ? "disabled" : ""}>+ add rule</button>
    `)}
    ${currentBracket()?.bracket_kind === "official" ? collapsible("tiebreakers", "Season tiebreakers", `
      <p class="muted">Applies only to Official's season standings, when a season ends with players tied on points. Tried in order — the first layer that separates two players decides. Leave all "None", or exhaust every layer without a difference, and they share the placing — same as a per-show tie.</p>
      <div class="grid2">
        ${[0,1,2].map(i => tiebreakerSelectRow(i, (cfg.tiebreakers||[])[i] || "")).join("")}
      </div>
      <p class="muted" style="margin-top:6px;font-size:.78rem">Fewest zeros — any show in scope worth 0 points, including one never picked at all, counts against you (scoped from when you joined the season roster, not the season's start). Most laurels — per-show ties still share the crown. Highest single-show score.</p>
    `) : ""}
    <div class="panel">
      <button class="btn" onclick="saveConfig()">Save rules</button>
      <div class="err" id="cfg-err"></div>
      <p class="muted" style="margin-top:6px">Saves the three sections directly above —
        Game rules, House rules and Season tiebreakers — whether or not they're
        currently expanded. Voting &amp; scoring mode has its own save. Rule changes
        apply on the next scoring run; don't change mid-show unless you enjoy
        arguments.</p>
    </div>
    ${state.session.is_global_admin ? adminLine : ""}
    ${state.session.is_global_admin ? collapsible("data", "Manual sync &amp; scoring", `
      <div class="row">
        <button class="btn ghost small" onclick="runEdge('sync_shows', this)">Sync shows</button>
        <button class="btn ghost small" onclick="runEdge('sync_songs', this)">Sync song catalog</button>
        <button class="btn ghost small" onclick="runEdge('score', this)">Run scoring now</button>
      </div>
      <p class="muted" style="margin-top:8px">Scoring also runs automatically on the cron schedule. These are manual overrides.</p>
    `) : ""}
    ${globalConsoleHtml()}
    ${accountLine}
    ${settingsPanelHtml()}
    ${footerHtml()}`;
  if ((shows||[]).length) loadRoster();
  loadMembers();
  if (state.session.is_global_admin) loadGlobalLeagues();
  wireSettingsPanel();
}
export async function loadMembers(){
  // Scoped to the current league via admin_list_members (Stage C2b) —
  // replaces the old app-wide players_public read, which listed every
  // registered player regardless of league membership and let Boot fire
  // against people who weren't actually in this league. is_league_admin
  // restores the ★ marker the pre-2.0 build had.
  //
  // Row shape (admin-panel.md → "Members panel cleanup", LOCKED 2026-10-26,
  // ROWS A): two lines. Line one: star or dot, the name at FULL WIDTH
  // (.mname, deliberately not .nm — admin lists never truncate; the live
  // 27-character names must read in full), the "admin" marker, and a
  // "casual only" pill ONLY when official_opt_in is false. The old grey
  // "Official" label went: once Stage F made opt-in the default it sat on
  // nearly every row, said nothing, and wrapped the buttons. Line two:
  // Reset PIN and Boot, right-aligned. The viewer's own row has no
  // buttons, so it carries "you" on line one and no line two at all.
  const rows = await rpc("admin_list_members", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId });
  const args = p => "'" + p.player_id + "', " + jsArg(p.name);
  $("#playerlist").innerHTML = (rows||[]).map(p => `
    <div class="pickres hit member-row">
      <div class="member-l1"><span class="member-mark">${p.is_league_admin ? "★" : "·"}</span><span class="mname" title="${esc(p.name)}">${esc(p.name)}</span>${
        p.is_league_admin ? '<small class="muted">admin</small>' : ""}${
        p.player_id===state.session.id ? '<small class="muted">you</small>' : ""}${
        p.official_opt_in === false ? '<span class="pill casual-only">casual only</span>' : ""}</div>
      ${p.player_id===state.session.id ? "" : `<div class="member-l2">
        <button class="btn ghost small" onclick="resetMemberPin(${args(p)})">Reset PIN</button>
        <button class="btn ghost small" onclick="bootPlayer(${args(p)})" style="border-color:var(--coral);color:var(--coral)">Boot</button>
      </div>`}
    </div>`).join("") || '<p class="muted">Nobody here yet.</p>';
  loadUnaffiliated();
}

// Registered players who belong to NO league. Before this there was no
// admin-facing signal that anyone had registered at all — see
// sql/stage_q_unaffiliated_players.sql for why nothing else could supply it.
//
// The heading states what the list IS, not what it is FOR: the app has no
// idea which league someone wants, or whether they want one at all, so
// "waiting to join" would be a claim it cannot support. The caution line is
// the real mitigation and belongs on screen rather than only in a briefing:
// with two leagues, both admins see this same list, and nothing stops each
// adding the same person to a different league — silently, since a second
// add is a different (league_id, player_id) row and cannot conflict. The
// player then sits in two leagues, opted into both Official brackets,
// having asked for one.
//
// Failures render inline rather than throwing: this is a supplementary
// panel, and a lookup failure here must not take down the member list above
// it, which is the section's actual job.
//
// Stage W (onboarding.md decision 7, decision 2): the list comes newest
// first, each row with its registration date and a "new" mark inside
// NEW_SIGNUP_DAYS. These same rows set the Admin-tab badge, so the badge
// and the marks on screen never disagree. Rows without created_at (the
// pre-Stage-W function) render without a date and set no badge.
//
// Stage X (decision 8): each row also carries has_played and this admin's
// cleared_at. "Clear all" shows exactly while the badge is lit (D8). Remove
// shows only on a row the server says has never played, and a row that has
// played gets a "has played" note instead (D3); the server checks again on
// Remove. Against the Stage W function the rows carry neither column, so
// none of the three appear: has_played is tested against true and false
// exactly, and Clear all also needs a cleared_at key on some row.
//
// Row shape (decision 8, layout fix 2026-10-28): the Members panel's two
// lines, its classes reused. Line one is the name alone, in full (.mname,
// never .nm: admin lists never truncate, and a name with no spaces wraps).
// Line two holds the date and the "new" mark or "has played" note on the
// left (.signup-meta), and Add and Remove on the right. Every row renders
// both lines and the meta span, even empty, so the rows never differ in
// shape. The old single line ellipsised long names and wrapped Remove.
const fmtRegistered = iso => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
};
export async function loadUnaffiliated(){
  const box = $("#unaffiliated");
  if (!box) return;
  let rows;
  try{
    rows = await rpc("admin_list_unaffiliated_players", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId });
  }catch(e){
    box.innerHTML = `<p class="err">Couldn't load registered players: ${esc(e.message)}</p>`;
    return;
  }
  setSignupRows(rows);
  const opts = { nowMs: Date.now(), days: NEW_SIGNUP_DAYS, snoozeDays: SIGNUP_SNOOZE_DAYS };
  const n = (rows||[]).length;
  if (!n){ box.innerHTML = ""; return; }
  const canClear = rows.some(r => r && "cleared_at" in r) && signupBadgeLit();
  box.innerHTML = `
    <p class="muted" style="margin-top:14px;font-weight:600">Registered, not in any league (${n})${canClear
      ? ` <button class="btn ghost small" onclick="clearNewSignups()" title="Stop marking these as new for ${SIGNUP_SNOOZE_DAYS} days">Clear all</button>` : ""}</p>
    <p class="muted" style="font-size:.78rem">These people have accounts but no league.
      They may not have asked to join <b>this</b> one — add someone only if they
      contacted you.</p>
    ${(rows||[]).map(p => `
      <div class="pickres member-row signup-row">
        <div class="member-l1"><span class="mname" title="${esc(p.name)}">${esc(p.name)}</span></div>
        <div class="member-l2">
          <span class="signup-meta">${p.created_at ? `<span class="reg" title="Registered">${esc(fmtRegistered(p.created_at))}</span>` : ""}${
            isNew(p, opts) ? '<span class="pill new-signup">new</span>' : ""}${
            p.has_played === true ? '<span class="played" title="Has picks, scores, a season roster or an admin action, so it cannot be removed here">has played</span>' : ""}</span>
          <button class="btn ghost small" onclick="addMember('${p.player_id}', ${jsArg(p.name)})">Add</button>${p.has_played === false
            ? `<button class="btn ghost small" onclick="removeAccount('${p.player_id}', ${jsArg(p.name)})" style="border-color:var(--coral);color:var(--coral)">Remove</button>` : ""}
        </div>
      </div>`).join("")}`;
}
// Decision 8a (D1, D10): snoozes this admin's badge and current "new" marks
// for SIGNUP_SNOOZE_DAYS, stored on the server per admin, so another device
// and another admin are unaffected. The list reload re-sets the badge.
export async function clearNewSignups(){
  try{
    await rpc("admin_clear_new_signups", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId });
    toast(`New sign-ups cleared for ${SIGNUP_SNOOZE_DAYS} days`, "score");
  }catch(e){ toast(esc(e.message)); }
  loadUnaffiliated();
}
// Decision 8c–8e (D3, D5, D6): deletes an account that is in no league and
// has never played, which frees the name to register again. The server
// checks all of it again and logs the removal. Only Global is asked about a
// ban; the server refuses one from anyone else. The list reloads either
// way, since a refusal usually means the row was stale (someone added them,
// or they just played). confirm() is plain text, so the raw name is right
// there; toast() renders HTML, so the name is escaped.
export async function removeAccount(id, name){
  if (!confirm(`Remove ${name}'s account?\n\nThey are in no league and have never played. The account is deleted and the name becomes free to register again. This can't be undone.`)) return;
  const ban = !!state.session.is_global_admin
    && confirm(`Also block the name "${name}" app-wide?\n\nOK = remove + ban · Cancel = remove only`);
  try{
    const r = await rpc("admin_remove_unplayed_account", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_player_id:id, p_ban_name:ban });
    toast(`${esc(name)} removed${r?.name_banned ? " and banned" : ""}`, "score");
  }catch(e){ toast(esc(e.message)); }
  loadUnaffiliated();
}
// Session 4 step 4 — server-generates the new PIN (this admin never chooses
// it), returned once for relay; the reset target is forced to set a real
// PIN on next login (must_change_pin, Session 4 step 2). Uses alert(), not
// toast(): toast() auto-dismisses after 6s and caps at 4 visible — wrong for
// a value that has to be read and relayed carefully with no recovery if
// missed. alert() blocks until dismissed, and is already a no-op stub in
// the test harness, so no new test plumbing is needed for it.
export async function resetMemberPin(id, name){
  if (!confirm(`Reset ${name}'s PIN? They'll be forced to set a new one on next login. Relay the new PIN to them directly — it can't be recovered after this.`)) return;
  try{
    const r = await rpc("admin_reset_player_pin", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_player_id:id });
    alert(`New PIN for ${name}: ${r.new_pin}\n\nRelay this to them now — it will not be shown again.`);
  }catch(e){ toast(esc(e.message)); }
}
export async function searchMembers(){
  const q = $("#member-search").value.trim();
  if (q.length < 2){ $("#member-results").innerHTML = ""; return; }
  try{
    const rows = await rpc("admin_find_players", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_query:q });
    $("#member-results").innerHTML = (rows||[]).map(p => `
      <div class="pickres">
        <span class="nm" title="${esc(p.name)}">${esc(p.name)}</span>
        <span class="pt"><button class="btn ghost small" onclick="addMember('${p.player_id}', ${jsArg(p.name)})">Add</button></span>
      </div>`).join("") || '<p class="muted">No matches.</p>';
  }catch(e){ $("#member-results").innerHTML = `<p class="err">${esc(e.message)}</p>`; }
}
// Stage W (onboarding.md decision 7, Part B): when an Official season is
// running (its roster already snapshotted), the same call puts the new
// member on that season's roster, and rostered_seasons names it. The toast
// says so, which keeps the roster change an explicit admin action, and an
// open "manage roster" panel is redrawn. The name is escaped because toast()
// renders HTML and a registrant chooses their own name.
export async function addMember(id, name){
  try{
    const r = await rpc("admin_add_league_member", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_player_id:id });
    const seasons = Array.isArray(r?.rostered_seasons) ? r.rostered_seasons.map(s => esc(s.name)) : [];
    toast(seasons.length
      ? `${esc(name)} added, and to the ${seasons.join(" and ")} roster${seasons.length > 1 ? "s" : ""}`
      : `${esc(name)} added`, "score");
    $("#member-search").value = ""; $("#member-results").innerHTML = "";
    loadMembers();
    for (const sid of Object.keys(rosterOpen)) if (rosterOpen[sid]) renderRosterPanel(Number(sid));
  }catch(e){ toast(esc(e.message)); }
}
// Stage S — LEAGUE-scoped, not bracket-scoped: the links belong to the
// league and both brackets share one community (docs/decisions/onboarding.md).
// Sits in the league-scoped group between Members and Seasons; Seasons must
// stay immediately above the EDITING divider (docs/decisions/admin-panel.md).
// admin_update_league_config MERGES (config || p_data) and whitelists keys
// server-side, so unlike admin_update_config there is no whole-object
// hazard here and nothing to spread from state. An emptied field sends JSON
// null, which the server strips — i.e. "clear this link".
export async function saveLeagueConfig(){
  $("#lc-err").textContent = "";
  const val = id => { const v = $(id).value.trim(); return v === "" ? null : v; };
  const data = { fb_url: val("#lc-fb"), discord_url: val("#lc-discord") };
  for (const [k, v] of Object.entries(data)){
    if (v !== null && !/^https:\/\//i.test(v)){
      $("#lc-err").textContent = `${k === "fb_url" ? "Facebook" : "Discord"} link must start with https://`;
      return;
    }
  }
  try{
    await rpc("admin_update_league_config", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_data:data });
    // my_leagues is the client's only source of these values. Patch every
    // row of this league (both brackets) so a bracket switch re-renders the
    // section with what was actually saved rather than what was loaded.
    for (const row of state.leagues) if (row.league_id === state.currentLeagueId) Object.assign(row, data);
    toast("Links saved ✔", "score");
  }catch(e){ $("#lc-err").textContent = e.message; }
}
export function seasonRow(se){
  const v = se || { id:"", name:"", start_date:"", end_date:"" };
  return `<div class="admin-slot" data-season="${v.id}">
    <input class="k" placeholder="Name (e.g. Summer Tour 2026)" value="${esc(v.name)}">
    <input type="date" value="${v.start_date}" style="width:130px">
    <input type="date" value="${v.end_date}" style="width:130px">
    <button class="btn ghost small" onclick="saveSeason(this.parentElement)">Save</button>
    ${v.id ? `<button class="btn ghost small" onclick="deleteSeason(${v.id})" style="border-color:var(--coral);color:var(--coral)">✕</button>` : ""}
  </div>
  ${v.id ? `<div style="margin:0 0 10px">
    <button class="linkbtn" id="roster-toggle-${v.id}" onclick="toggleRoster(${v.id})">manage roster</button>
    <div id="roster-panel-${v.id}" class="hidden" style="margin-top:6px"></div>
  </div>` : ""}`;
}
// Opt-in override for a running Official season — add/remove a player from
// season_rosters directly (bypasses the live-flag lock, per the admin
// override rule in CLAUDE.md). Kept as plain module state, not state.js:
// purely a UI expand/collapse flag, not app data.
const rosterOpen = {};
async function renderRosterPanel(seasonId){
  const panel = $("#roster-panel-"+seasonId);
  if (!panel) return;
  panel.innerHTML = '<p class="muted">Loading…</p>';
  try{
    const [members, roster] = await Promise.all([
      rpc("admin_list_members", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId }),
      rpc("admin_list_season_roster", { p_name:state.session.name, p_pin:state.session.pin, p_season_id:seasonId }),
    ]);
    const onRoster = new Set((roster||[]).map(r => r.player_id));
    panel.innerHTML = (members||[]).map(m => `
      <div class="pickres ${onRoster.has(m.player_id)?"hit":"miss"}">
        <span>${onRoster.has(m.player_id)?"✔":"—"}</span><span class="nm" title="${esc(m.name)}">${esc(m.name)}</span>
        <span class="pt"><button class="btn ghost small" onclick="setRosterMember(${seasonId}, '${m.player_id}', ${!onRoster.has(m.player_id)}, ${jsArg(m.name)})">${onRoster.has(m.player_id)?"Remove":"Add"}</button></span>
      </div>`).join("") || '<p class="muted">No members in this league yet.</p>';
  }catch(e){ panel.innerHTML = `<p class="err">${esc(e.message)}</p>`; }
}
export async function toggleRoster(seasonId){
  rosterOpen[seasonId] = !rosterOpen[seasonId];
  const toggleBtn = $("#roster-toggle-"+seasonId), panel = $("#roster-panel-"+seasonId);
  if (toggleBtn) toggleBtn.textContent = rosterOpen[seasonId] ? "hide roster" : "manage roster";
  if (panel) panel.classList.toggle("hidden", !rosterOpen[seasonId]);
  if (rosterOpen[seasonId]) renderRosterPanel(seasonId);
}
// Adding is idempotent, cheap and non-destructive — no confirm. REMOVAL is
// the destructive half and had none until 2026-08-17. What it actually does
// is easy to get wrong in both directions, so the wording below is precise
// rather than scary: the player does NOT vanish from the standings (inScope
// filters on the season's date range and never reads rosterJoinDates, so
// their points, shows, high and wins all survive), removal does not stop
// their zeros and can increase them, and a later re-add resets added_at —
// silently dropping every zero accumulated before that point, which is a
// standings edit in the player's FAVOUR and cannot be undone.
export async function setRosterMember(seasonId, playerId, add, name){
  if (!add){
    // Same blocking-catch discipline as bootPlayer: the lock note below
    // changes what the admin is told, so guessing at it is worse than
    // stopping. Looked up per invocation, never from renderAdmin's snapshot.
    let season = null;
    try{
      const seasons = await rpc("get_bracket_seasons", { p_bracket_id: officialBracketId() });
      season = (seasons||[]).find(s => Number(s.id) === Number(seasonId));
      if (!season) throw new Error("season not found");
    }catch(e){
      toast("Couldn't load this season — removal cancelled. Try again.");
      return;
    }
    const lockNote = season.roster_locked_at
      ? `\n\nThis season is already activated, so nothing will re-add them `
        + `automatically — only a manual re-add here.`
      : "";
    if (!confirm(
      `Remove ${name || "this player"} from "${season.name}"?\n\n`
      + `They stop accruing points — their picks for this season's remaining `
      + `shows won't be scored.\n\n`
      + `Their existing scores stay on the board and keep counting toward their `
      + `season total. They do not disappear from the standings.\n\n`
      + `This does NOT stop their zeros, and can increase them: losing their `
      + `join date widens the zero window back to the season's start.\n\n`
      + `If you re-add them later, their join date resets to that moment and `
      + `every zero they'd built up before then is permanently dropped. The `
      + `original date cannot be restored.` + lockNote)) return;
  }
  try{
    await rpc("admin_set_season_roster", { p_name:state.session.name, p_pin:state.session.pin, p_season_id:seasonId, p_player_id:playerId, p_add:add });
    toast(add ? "Added to roster" : "Removed from roster", "score");
    renderRosterPanel(seasonId);
  }catch(e){ toast(esc(e.message)); }
}
export function addSeasonRow(){ $("#seasonrows").insertAdjacentHTML("beforeend", seasonRow(null)); }
export async function saveSeason(row){
  const [name, start, end] = [...row.querySelectorAll("input")].map(i => i.value);
  const id = row.dataset.season ? Number(row.dataset.season) : null;
  try{
    await rpc("admin_save_season", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:officialBracketId(), p_id:id, p_sname:name, p_start:start||null, p_end:end||null });
    toast("Season saved ✔", "score"); state.boardSeason = null; renderAdmin();
  }catch(e){ toast(esc(e.message)); }
}
export async function deleteSeason(id){
  if (!confirm("Delete this season? Scores are untouched — only the grouping goes away.")) return;
  try{
    await rpc("admin_delete_season", { p_name:state.session.name, p_pin:state.session.pin, p_id:id });
    toast("Season deleted", "score"); state.boardSeason = null; renderAdmin();
  }catch(e){ toast(esc(e.message)); }
}
let bansOpen = false;
export async function toggleBans(){
  bansOpen = !bansOpen;
  $("#banToggle").textContent = bansOpen ? "hide ban list" : "show ban list";
  $("#banlist").classList.toggle("hidden", !bansOpen);
  if (!bansOpen) return;
  $("#banlist").innerHTML = '<p class="muted">Loading…</p>';
  try{
    const rows = await rpc("admin_list_bans", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId });
    $("#banlist").innerHTML = (rows||[]).map(r => `
      <div class="pickres miss"><span>⛔</span><span class="nm" title="${esc(r.name)}">${esc(r.name)}</span>
        <span class="pt"><small class="muted">${new Date(r.banned_at).toLocaleDateString()}</small>
          <button class="btn ghost small" onclick="unban(${jsArg(r.name)})">Unban</button></span>
      </div>`).join("") || '<p class="muted">Nobody is banned. A peaceful kingdom.</p>';
  }catch(e){ $("#banlist").innerHTML = `<p class="err">${esc(e.message)}</p>`; }
}
export async function unban(name){
  if (!confirm(`Unban "${name}"? The name becomes registerable again.`)) return;
  try{
    await rpc("admin_unban", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_banned:name });
    toast(`${esc(name)} unbanned`, "score");
    bansOpen = false; toggleBans();
  }catch(e){ toast(esc(e.message)); }
}
// The confirm text below states consequences rather than asking "are you
// sure", because the consequence is the non-obvious part: booting does NOT
// stop a player accruing zeros in a season that is currently running (see
// the boot entry in CLAUDE.md), and there is no workaround available in the
// app today. The previous wording said they "just stop being able to submit
// new ones", which is true about points and false about zeros.
export async function bootPlayer(id, name){
  // Season state is looked up HERE, per invocation — deliberately NOT read
  // from renderAdmin's `seasonsA`, which is a snapshot from whenever the
  // panel last rendered and is wrong the moment a season starts or ends.
  // No show count is shown for the same reason: a cached one would go stale
  // as soon as a show scored, and a live one is a second round trip.
  // A FAILED LOOKUP BLOCKS THE BOOT. It deliberately does not fall through
  // to a warning the admin can click past: the two branches below differ by
  // "this is harmless" vs "this permanently costs them standings position",
  // and a failure that silently produced the harmless-sounding wording would
  // be the worst possible outcome — an irreversible action taken under a
  // reassurance the code never actually verified. Boot is never urgent
  // enough to justify guessing; retrying costs one click.
  let live = null;
  try{
    const seasons = await rpc("get_bracket_seasons", { p_bracket_id: officialBracketId() });
    const today = new Date().toLocaleDateString('sv');
    live = (seasons||[]).find(s => s.start_date <= today && today <= s.end_date) || null;
  }catch(e){
    toast("Couldn't check whether a season is running — boot cancelled. Try again.");
    return;
  }

  const head = `${name} will lose access to this league.\n\n`
    + `Their existing picks and scores stay on the books, and standings for `
    + `seasons that have already ended are unaffected.\n\n`;
  const tail = live
    ? `"${live.name}" is running. They stay on its roster and will score a zero `
      + `for every remaining show in it, counting against them under the `
      + `"fewest zeros" tiebreaker.\n\n`
      + `There is currently NO way to prevent this. Removing them from the season `
      + `roster is not a workaround: they keep counting because they already have `
      + `scores in this bracket, and it can make it worse. Re-adding them `
      + `afterwards permanently drops the zeros they'd built up — that's a `
      + `standings edit, not a repair. Boot now only if that's acceptable.`
    : `No Official season is running, so no zeros will accrue. They won't be `
      + `added to future season rosters either.`;
  if (!confirm(head + tail)) return;
  const ban = confirm(`Also block the name "${name}" from rejoining this league?\n\nOK = remove + ban · Cancel = remove only`);
  try{
    await rpc("admin_league_boot", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_player_id:id, p_ban:ban });
    toast(`${esc(name)} removed${ban ? " and banned" : ""}`, "score");
    loadMembers(); loadRoster();
  }catch(e){ toast(esc(e.message)); }
}
// Slot keys a bracket renders for a given format. Mirrors slotDefs()'s
// section selection exactly — `(format === "one_set" && cfg.oneset) ?
// cfg.oneset : cfg` — so an absent oneset section correctly falls back to
// the top-level one rather than being treated as empty.
// Returns null for ranked brackets: rank keys come from cfg.ranked.ladder at
// config top level and never through the oneset section, so a ranked bracket
// is format-INDEPENDENT and cannot be orphaned by a toggle. Counting it
// would be false, not merely noisy.
function slotKeysFor(cfg, format){
  if ((cfg.mode || "slots") === "ranked_choice") return null;
  const sect = (format === "one_set" && cfg.oneset) ? cfg.oneset : cfg;
  const keys = (sect.slots || []).map(s => s.key);
  for (let i = 1; i <= (sect.flat_picks || 0); i++) keys.push("flat" + i);
  return keys;
}
function slotKeyLabel(cfg, format, key){
  const sect = (format === "one_set" && cfg.oneset) ? cfg.oneset : cfg;
  const hit = (sect.slots || []).find(s => s.key === key);
  if (hit) return hit.label || key;
  const m = /^flat(\d+)$/.exec(key);
  return m ? `Flat pick ${m[1]}` : key;
}

// A format toggle swaps which config section supplies the valid slot keys,
// so any stored pick keyed to a slot the NEW section doesn't define becomes
// unaddressable — and is then destroyed by submit_picks' catch-all delete on
// the player's next save. This confirm does not prevent that; it makes the
// consequence visible before the click, the same job bootPlayer's and
// setRosterMember's dialogs do.
//
// MAINTAINER NOTE, not something to put in the dialog: one_set -> standard
// currently orphans nothing for Ambassadors Official ONLY because its oneset
// keys (opener/closer/cover1 + 1 flat) happen to be a subset of its standard
// keys (opener/closer/encore/cover1 + 2 flats). That is a property of one
// bracket's current config, not a guarantee — editing the one-set slots to
// include a type the standard section lacks makes the reverse direction
// orphan too. Hence the key sets are computed both ways rather than the
// direction being hardcoded as safe.
export async function toggleFormat(showId, next){
  let affected = [];
  try{
    const show = await fetchShow(showId);
    const current = show?.format || "standard";
    if (current === next){ return; } // clicking the already-active button
    const { data: brackets, error } = await db.from("brackets")
      .select("id,name,kind,config").eq("league_id", state.currentLeagueId);
    if (error) throw new Error(error.message);
    for (const b of brackets || []){
      const cur = slotKeysFor(b.config || {}, current);
      const nxt = slotKeysFor(b.config || {}, next);
      if (!cur || !nxt) continue;                       // ranked: immune
      const lost = cur.filter(k => !nxt.includes(k));
      if (!lost.length) continue;                       // this bracket keeps every key
      // admin_pick_status is the ONLY count available here: get_show_picks is
      // cutoff-gated (now() >= cutoff_at) while the shows that matter are by
      // definition still open, so it returns zero rows for every one of them.
      // saveConfig's orphan check uses get_show_picks and is silently broken
      // for exactly that reason — see CLAUDE.md.
      const rows = await rpc("admin_pick_status", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:b.id, p_show_id:showId });
      const n = (rows||[]).reduce((sum, r) => sum + (r.picks_count || 0), 0);
      if (n > 0) affected.push({ name: b.name, n, lost: lost.map(k => slotKeyLabel(b.config, current, k)) });
    }
  }catch(e){
    // Blocks rather than warning-and-allowing, same as bootPlayer and
    // setRosterMember: the two outcomes differ by "nothing is at risk" and
    // "N picks lose a slot", and a failed lookup that produced the
    // reassuring one would be an irreversible action taken under a
    // reassurance nothing verified.
    toast("Couldn't check which picks this would orphan — format unchanged. Try again.");
    return;
  }
  if (affected.length){
    const body = affected.map(a =>
      `• ${a.name} — ${a.n} pick${a.n === 1 ? "" : "s"} saved; these slots disappear: ${a.lost.join(", ")}`).join("\n");
    if (!confirm(
      `Switching this show to ${next === "one_set" ? "1 set" : "2 set"} removes slots that already hold picks.\n\n`
      + `${body}\n\n`
      + `Picks on those slots stop being addressable, and are deleted the next time that player saves. `
      + `Nothing in the app re-keys them.\n\n`
      + `(The counts are all picks saved for this show in that bracket — the app cannot tell, before cutoff, `
      + `how many sit specifically on the disappearing slots.)\n\n`
      + `Switch anyway?`)) return;
  }
  try{
    await rpc("admin_set_show_format", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_show_id:showId, p_format:next });
    toast("Format: " + (next === "one_set" ? "1 set" : "2 set"), "score");
    renderAdmin();
  }catch(e){ toast(esc(e.message)); }
}
// Who's Picked (admin-panel.md → "Who's Picked", LOCKED 2026-10-25). Two
// lines per row: name + status pill, then the saved text. Status is derived
// EXACTLY the way shows.js's pickButtonInfo does it — saved count against
// slotDefs(format).length, with "done" meaning saved >= target — so this
// panel and the pick button can never disagree about "done". The Show chip
// is a chase list: module state, reset to "needs" by every renderAdmin (so
// each load, and each realtime re-render, opens on the people still to
// chase), kept only across show-select changes within one render. Never
// persisted. The RPC returns every league member (left join), so "needs"
// is a filter over a complete list, not a separate query.
let rosterFilter = "needs";
export function setRosterFilter(f){ rosterFilter = f === "all" ? "all" : "needs"; loadRoster(); }
export async function loadRoster(){
  const showId = Number($("#roster-show").value);
  if (!showId) return;
  try{
    const [rows, show] = await Promise.all([
      rpc("admin_pick_status", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_show_id:showId }),
      fetchShow(showId),
    ]);
    const target = slotDefs(show?.format).length;
    const statusOf = r => (target > 0 && r.picks_count >= target) ? "done" : (r.picks_count > 0 ? "partial" : "none");
    // Pill ladder (admin-panel.md → Who's Picked, PILL refinement
    // 2026-10-26): Done mint, Partial yolk, Waiting coral. The status key
    // stays "none" (data-status, statusOf); only the pill reads "Waiting
    // on" — "No picks" repeated line two's "no picks yet".
    const PILL = { done: ["done", "Done"], partial: ["partial", "Partial"], none: ["waiting", "Waiting on"] };
    const MARK = { done: "✔", partial: "✓", none: "—" };
    const total = rows.length, done = rows.filter(r => r.picks_count > 0).length;
    const shown = rosterFilter === "all" ? rows : rows.filter(r => statusOf(r) !== "done");
    const chip = `<div class="chiprow"><span class="lbl">Show</span><div class="switcher">
        <button class="linkbtn switcher-btn${rosterFilter==="needs"?" on":""}" onclick="setRosterFilter('needs')">Needs picks</button>
        <button class="linkbtn switcher-btn${rosterFilter==="all"?" on":""}" onclick="setRosterFilter('all')">All</button></div></div>`;
    // An empty chase list must SAY so, not render nothing (the empty-set
    // rule): everyone done is good news, no members is a different fact.
    const empty = !rows.length
      ? '<p class="muted" id="roster-empty">No members in this league yet.</p>'
      : '<p class="muted" id="roster-empty">Everyone has picks in — nobody to chase.</p>';
    // Line two keeps the original "n picks · saved <when>" text verbatim,
    // bar one grammar fix: a single pick reads "1 pick", not "1 picks".
    $("#roster").innerHTML = `
      <p class="muted" style="margin-bottom:6px"><b style="color:var(--mint)">${done}</b> of ${total} players have picks in</p>
      ${chip}
      ${shown.map(r => { const st = statusOf(r); return `<div class="pickres roster-row ${st==="none"?"miss":"hit"}" data-status="${st}">
        <span>${MARK[st]}</span>
        <div class="roster-lines">
          <div class="roster-l1"><span class="nm" title="${esc(r.player_name)}">${esc(r.player_name)}</span><span class="pill ${PILL[st][0]}">${PILL[st][1]}</span></div>
          <div class="roster-l2 pt">${r.picks_count>0
            ? r.picks_count+(r.picks_count===1?" pick":" picks")+" · saved "+new Date(r.last_saved).toLocaleString(undefined,{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})
            : "no picks yet"}</div>
        </div>
      </div>`; }).join("") || empty}`;
  }catch(e){ $("#roster").innerHTML = `<p class="err">${esc(e.message)}</p>`; }
}
// Player-facing labels are fixed/code-owned (src/core/slotTypes.js) — the
// admin only ever picks a TYPE and a point value now. The dropdown option
// text is built from that same label+tooltip data (not a separately
// maintained description string), so there's one source of truth instead
// of two copies that can drift. One-set sections exclude set1_closer/
// set2_opener entirely (see ONE_SET_EXCLUDED_TYPES) rather than just
// relabeling them — a one-set show can't ever satisfy either slot.
export function adminSlotRow(sl, format){
  const t = sl.type || sl.key;
  const excluded = format === "one_set" ? ONE_SET_EXCLUDED_TYPES : [];
  let opts = Object.keys(SLOT_LABELS).filter(k => !excluded.includes(k)).map(k =>
    `<option value="${k}" ${k===t?"selected":""}>${esc(slotLabelFor(k, format))} — ${esc(SLOT_TOOLTIPS[k])}</option>`).join("");
  if (t && !(t in SLOT_LABELS)) opts += `<option value="${esc(t)}" selected>${esc(t)} (legacy)</option>`;
  return `<div class="admin-slot">
    <select class="k" title="which position this slot scores against">${opts}</select>
    <input class="p" type="number" min="0" value="${sl.points}">
    <button class="btn ghost small" onclick="this.parentElement.remove()">✕</button></div>`;
}
export function addSlot(target){
  const format = target === "slots1" ? "one_set" : "standard";
  $("#"+target).insertAdjacentHTML("beforeend", adminSlotRow({ key:"encore", points:2 }, format));
}
export function readSlots(containerId){
  let coverN = 0;
  return [...document.querySelectorAll('#'+containerId+' .admin-slot')].map(r => {
    const type = r.querySelector("select.k").value;
    const points = Number(r.querySelector("input.p").value);
    const key = type === "cover_pick" ? "cover" + (++coverN) : type;
    return { key, type, points };
  }).filter(sl => sl.type);
}
// Only rendered on the Official bracket (see renderAdmin) — three ordered
// slots rather than a drag-reorderable list, matching this codebase's
// existing <select>-based config-panel style.
function tiebreakerSelectRow(idx, current){
  const opts = ["", ...Object.keys(TIEBREAK_LABELS)].map(k =>
    `<option value="${k}" ${k===current?"selected":""}>${k ? esc(TIEBREAK_LABELS[k]) : "None"}</option>`).join("");
  return `<div class="field"><label>${["1st","2nd","3rd"][idx]} tiebreaker</label><select id="tb-${idx}">${opts}</select></div>`;
}
// undefined (not [] ) when the picker isn't on the page at all (viewing
// Casual, which has no seasons) — saveConfig then omits the key entirely
// rather than writing a meaningless tiebreakers field onto Casual's config.
// A duplicate pick across the three slots is silently deduped to its first
// (higher-priority) position rather than rejected — unlike the slot-type
// dupe check, there's no ambiguous game state to guard against here.
function readTiebreakers(){
  const els = [0,1,2].map(i => $("#tb-"+i));
  if (!els[0]) return undefined;
  const seen = new Set(), list = [];
  for (const el of els){
    const v = el.value;
    if (v && !seen.has(v)){ seen.add(v); list.push(v); }
  }
  return list;
}
// Master switch owns its own save as of 2026-08-17. Previously its three
// fields (voting override, scoring mode, perfect-sheet bonus) were committed
// by the "Save all rules" button four sections below — a control whose
// consequences weren't visible from where you acted, which is the defect
// this splits.
//
// The cost, stated because it is real: admin_update_config writes the WHOLE
// config object, so both save paths must merge against state.cfg rather than
// build from scratch. That is the delicate logic saveConfig documents below
// (a literal fallback is a data-loss path, not a harmless default), and
// there are now two places honouring it instead of one. Both spread
// ...state.cfg first for exactly that reason.
async function confirmModeChange(mode){
  if (mode === (state.cfg.mode || "slots")) return true;
  let atRisk = [];
  let lookupFailed = false;
  try{
    const shows = await fetchShows(q => q.gte("showdate", new Date(Date.now()-2*864e5).toISOString().slice(0,10)));
    const open = (shows||[]).filter(sh => showState(sh) === "open");
    // admin_pick_status, NOT get_show_picks — this check was silently dead
    // until 2026-08-21 and the reason is worth keeping: get_show_picks ends
    // `and ls.cutoff_at is not null and now() >= ls.cutoff_at`, while every
    // show reaching this line is `showState(sh) === "open"`, i.e. cutoff in
    // the FUTURE. Mutually exclusive, so it returned zero rows for every
    // show, atRisk was always empty, and the warning never once fired in
    // production. admin_pick_status is admin-gated but NOT cutoff-gated,
    // which is exactly what a pre-cutoff count needs.
    //
    // SUM picks_count; do not count rows. It returns one row per league
    // MEMBER via a left join, so members with zero picks are included and
    // a row count reports league size rather than picks at risk. Same trap
    // toggleFormat already handles.
    const counts = await Promise.all(open.map(sh =>
      rpc("admin_pick_status", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id: state.currentBracketId, p_show_id: sh.id })
        .then(rows => ({ show: sh, n: (rows||[]).reduce((sum, r) => sum + (r.picks_count || 0), 0) }))));
    atRisk = counts.filter(c => c.n > 0);
  }catch(e){ lookupFailed = true; }
  // A failed lookup gets its own confirm rather than falling through
  // silently. Not blocking the save on a network hiccup is the right
  // default, but "no picks are at risk" and "couldn't find out" must not
  // look the same to the admin.
  if (lookupFailed){
    return confirm(
      `Couldn't check whether existing picks would be orphaned.\n\n` +
      `Switching mode may erase picks players have already entered for open shows.\n\n` +
      `Switch anyway?`);
  }
  if (atRisk.length){
    const nPicks = atRisk.reduce((sum, c) => sum + c.n, 0);
    const venues = atRisk.map(c => c.show.venue || "TBA").join(", ");
    return confirm(
      `Switching scoring mode will orphan existing picks.\n\n` +
      `Saved picks are keyed to the current mode — opener/closer/… in slots mode, ` +
      `rank1/rank2/… in ranked. After the switch those keys stop matching, so anyone ` +
      `who has already picked for an open show sees a blank sheet and loses what they entered.\n\n` +
      `This affects ${nPicks} pick${nPicks === 1 ? "" : "s"} across ` +
      `${atRisk.length} open show${atRisk.length === 1 ? "" : "s"} (${venues}).\n\n` +
      `Nothing in the app re-keys them; it has to be done directly in the database.\n\n` +
      `Switch anyway?`);
  }
  return true;
}

export async function saveMasterSwitch(){
  $("#ms-err").textContent = "";
  const mode = $("#c-mode").value;
  if (!await confirmModeChange(mode)) return;
  const data = {
    ...state.cfg,
    voting_override: $("#c-override").value,
    mode,
    bonuses: { ...(state.cfg.bonuses || {}), perfect: Number($("#c-bperfect").value) },
  };
  // Empty-ladder gate, client side (admin-panel.md → "EMPTY RANKED LADDER",
  // LOCKED 2026-10-26, decision 2): switching to ranked with no stored
  // ladder writes the ladder TOGETHER with the mode — the one the editor
  // shows (it previews the default after onModeChange, and the admin may
  // have edited it) or the default itself — so ranked mode never lands
  // without rows. Stage V refuses that server-side regardless; this is
  // the courtesy that keeps the admin's own ladder instead of an error.
  if (mode === "ranked_choice" && !(state.cfg.ranked?.ladder ?? []).length){
    let ladder = DEFAULT_RANKED_LADDER;
    if ($("#rankladder")){
      const shown = readLadder();
      if (shown === null){ $("#ms-err").textContent = $("#cfg-err").textContent; return; }
      if (shown.length) ladder = shown;
    }
    data.ranked = { ladder };
  }
  try{
    await rpc("admin_update_config", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_data:data });
    state.cfg = data; toast("Master switch saved ✔", "score");
    // Mode selects which rules sections exist, so re-render them from what
    // was actually SAVED — same reasoning as saveConfig's re-render below.
    const region = $("#rules-region");
    if (region) region.innerHTML = rulesRegionHtml(data, data.mode);
  }catch(e){ $("#ms-err").textContent = e.message; }
}

export async function saveConfig(){
  $("#cfg-err").textContent = "";
  // Mode is owned by saveMasterSwitch now, so it is read from the saved
  // config rather than the select. Every branch below keys off whether an
  // INPUT is rendered, not off mode, so this changes nothing about which
  // values get read — only which save is authoritative for the field.
  const mode = state.cfg.mode || "slots";
  // Only one mode's rule sections are on screen at a time, so every read
  // below has to tolerate its input being absent — and must fall back to
  // the value ALREADY IN state.cfg, never to a literal like 0/false/[].
  // A literal fallback is a data-loss path, not a harmless default: switch
  // to ranked, save, switch back, and every slot, bonus and flag the admin
  // had configured would have been overwritten with zeros by the save that
  // happened while their inputs weren't rendered. Symmetric in both
  // directions — a slots-mode save has to preserve the ladder the same way.
  const slots  = $("#slots")  ? readSlots("slots")  : (state.cfg.slots ?? []);
  const slots1 = $("#slots1") ? readSlots("slots1") : (state.cfg.oneset?.slots ?? []);
  for (const arr of [slots, slots1]){
    const types = arr.filter(sl => sl.type !== "cover_pick").map(sl => sl.type);
    if (new Set(types).size !== types.length){
      $("#cfg-err").textContent = "Each slot type (except Cover pick) can only be used once per section."; return;
    }
  }
  // readLadder() signals invalid input with null (distinct from a legitimately
  // empty []), having already written the reason to #cfg-err. Checked here,
  // before the data object is assembled and before the RPC — a ternary that
  // let a failed read fall through as [] would silently wipe the ladder on a
  // single stray character, which is the same data-loss shape the fallbacks
  // above exist to prevent.
  let ladder;
  if ($("#rankladder")) {
    ladder = readLadder();
    if (ladder === null) return;
    if (mode === "ranked_choice" && !ladder.length){
      $("#cfg-err").textContent = "A ranked-choice bracket needs at least one rank. Add a rank, or switch back to slots."; return;
    }
  } else {
    ladder = state.cfg.ranked?.ladder ?? [];
  }
  // Changing scoring mode orphans existing picks: they're stored keyed by
  // slot ("opener"/"closer"/… in slots mode, "rank1"/"rank2"/… in ranked),
  // and after a switch those keys match nothing the new sheet renders. The
  // player opens a blank sheet and their entries are simply gone. This is
  // not hypothetical — it happened on 2026-08-13, and one player's six
  // picks had to be re-keyed by hand with direct SQL.
  //
  // The confirm lives HERE rather than on the select's onchange, on purpose.
  // onModeChange only re-renders the rules region locally; nothing a player
  // can see has changed and nothing is written, so a confirm there would
  // warn about a non-event and would fight an admin merely browsing the
  // dropdown. It would also contradict onModeChange's deliberate refusal to
  // touch state.cfg. Writing the config is the moment of consequence, and
  // cancelling here is clean: return early, nothing persisted, the select
  // keeps the admin's choice so they can simply change it back.
  //
  const tiebreakers = readTiebreakers();
  const data = {
    slots,
    mode,
    ranked: { ladder },
    custom_rules: readCustomRules(),
    flat_picks: Number($("#c-flat")?.value ?? state.cfg.flat_picks ?? 0),
    flat_points: Number($("#c-flatpts")?.value ?? state.cfg.flat_points ?? 1),
    partial_credit: $("#c-partial") ? $("#c-partial").value === "true" : !!state.cfg.partial_credit,
    partial_points: Number($("#c-partpts")?.value ?? state.cfg.partial_points ?? 1),
    allow_duplicates: $("#c-dupes") ? $("#c-dupes").value === "true" : !!state.cfg.allow_duplicates,
    // voting_override and bonuses.perfect are owned by saveMasterSwitch as of
    // 2026-08-17 and are carried through from state.cfg here, NOT read from
    // their inputs. Reading them in both saves would let the two panels
    // silently disagree: whichever saved last would win, so an edit made in
    // Master switch and saved there could be reverted by a later rules save
    // that happened to re-read a stale input. One writer per field.
    voting_override: state.cfg.voting_override,
    bonuses: {
      cover: Number($("#c-bcover")?.value ?? state.cfg.bonuses?.cover ?? 0),
      debut: Number($("#c-bdebut")?.value ?? state.cfg.bonuses?.debut ?? 0),
      perfect: Number(state.cfg.bonuses?.perfect ?? 0),
      jamchart: 0,
    },
    wildcards: { debut: $("#c-wcdebut") ? $("#c-wcdebut").value === "true" : (state.cfg.wildcards?.debut ?? true) },
    oneset: {
      slots: slots1,
      flat_picks: Number($("#c1-flat")?.value ?? state.cfg.oneset?.flat_picks ?? 0),
      flat_points: Number($("#c1-flatpts")?.value ?? state.cfg.oneset?.flat_points ?? 1),
    },
    ...(tiebreakers !== undefined ? { tiebreakers } : {}),
  };
  try{
    await rpc("admin_update_config", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_data:data });
    state.cfg = data; toast("Rules saved ✔", "score");
    // Re-render the rules region from what was actually SAVED, not from what
    // was typed. Both readers normalize: readLadder() drops blank rows and
    // readSlots() drops rows with no type, so a blank left in the middle of
    // the ladder shifts every rank below it up one. Without this the panel
    // would keep showing five rows while the database holds four, and the
    // admin would have no way to see which rank a pick now scores.
    const region = $("#rules-region");
    if (region) region.innerHTML = rulesRegionHtml(data, data.mode);
  }catch(e){ $("#cfg-err").textContent = e.message; }
}

// Row position is the rank, so order here is load-bearing: querySelectorAll
// returns document order, which is the visual order of the rows.
//
// EVERY RENDERED ROW MUST CARRY A VALUE. Removing a rank means clicking the
// row's ✕, not clearing its field — a rank's position is meaningful, so a
// blank row in the middle was never a coherent way to express deletion.
//
// Why empty and garbage produce the same message, which looks lazy and
// isn't: the input is type="number", and both real browsers and JSDOM
// coerce unparseable content to "" at `.value` (verified directly — setting
// .value = "abc", and even setAttribute("value","abc"), both read back as
// ""). So "empty because the admin cleared it" and "empty because the admin
// typed 1.2.3" are literally indistinguishable here, and `!Number.isFinite`
// is unreachable through the UI for that reason. `validity.badInput` is the
// standard way to tell them apart, but JSDOM reports it false
// unconditionally, so anything built on it would be untestable. Rejecting
// empty covers both causes with a message that's correct either way, which
// is strictly better than the alternative it replaced: silently dropping
// the row, leaving the admin to notice a rank had vanished.
//
// Returns null (NOT []) on rejection, after writing the reason to #cfg-err —
// saveConfig aborts on null, so nothing partial is ever written. Accepted
// values are coerced with Number() so the stored jsonb is numeric; a DOM
// scrape yields strings, and leaving them would make the scorer fix types on
// every scoring pass.
function readLadder(){
  const out = [];
  const rows = [...document.querySelectorAll("#rankladder .rank-pts")];
  for (let i = 0; i < rows.length; i++){
    const raw = rows[i].value.trim();
    if (raw === ""){
      $("#cfg-err").textContent = `Rank ${i+1} has no value — enter a number or remove the row.`;
      return null;
    }
    const n = Number(raw);
    // Unreachable from the UI (see above) but kept for a value that arrives
    // some other way — a devtools edit, or a future input type change.
    if (!Number.isFinite(n)){
      $("#cfg-err").textContent = `Rank ${i+1} isn't a number — enter a number or remove the row.`;
      return null;
    }
    out.push(n);
  }
  return out;
}
export async function saveCutoff(showId, btn){
  const input = document.querySelector(`input[data-show="${showId}"]`);
  if (!input.value) return;
  // input.value is venue-local wall-clock text when a timezone is known
  // (data-tz set) — must be interpreted as such, not as device-local, or
  // saving would reintroduce exactly the silent-shift bug this panel exists
  // to prevent. Falls back to the old device-local parse when the venue's
  // timezone isn't known (matches what's actually displayed in that case).
  const tz = input.dataset.tz;
  const cutoffISO = tz ? venueLocalToUTC(input.value, tz) : new Date(input.value).toISOString();
  try{
    await rpc("admin_set_cutoff", { p_name:state.session.name, p_pin:state.session.pin, p_league_id:state.currentLeagueId, p_show_id:showId, p_cutoff:cutoffISO });
    btn.textContent = "✔"; setTimeout(() => btn.textContent = "Change cutoff", 1500);
    // Best effort — the cutoff itself already saved above; a failed Discord
    // notice shouldn't read to the admin as a failed save, so this is
    // fire-and-forget rather than awaited inside the try/catch above it.
    edgeFn("cutoff_changed", { p_name:state.session.name, p_pin:state.session.pin, league_id:state.currentLeagueId, show_id:showId }).catch(() => {});
  }catch(e){ toast(esc(e.message)); }
}
// Clears a show's sync flag (see flagNotice in renderAdmin). Any league
// admin or global may clear — shows are global, so the RPC takes no league
// id. The RPC logs who cleared what (sync_flag_clears) before nulling the
// flag; this just re-renders so the notice disappears. Awaited, so a caller
// (the harness) sees the re-rendered Shows section when it resolves.
export async function clearSyncFlag(showId, btn){
  if (btn){ btn.disabled = true; btn.textContent = "…"; }
  try{
    await rpc("admin_clear_sync_flag", { p_name:state.session.name, p_pin:state.session.pin, p_show_id:showId });
    await renderAdmin();
  }catch(e){ toast(esc(e.message)); if (btn){ btn.disabled = false; btn.textContent = "Clear"; } }
}
export async function finalizeShow(showId, btn){
  if (!confirm("Run final scoring and mark this show complete? Picks and scores lock for good.")) return;
  btn.disabled = true; btn.textContent = "…";
  try{
    await edgeFn("finalize", { p_name:state.session.name, p_pin:state.session.pin, league_id:state.currentLeagueId, show_id:showId });
    toast("Show finalized 🏁", "score");
    renderAdmin();
  }catch(e){ toast(esc(e.message)); btn.disabled = false; btn.textContent = "Finalize"; }
}
// Un-finalizes a show so a correction to The Carton's setlist can be
// re-scored — pairs with the "fix the setlist on The Carton, then Reopen,
// then Finalize again" workflow (CLAUDE.md). The edge function's `reopen`
// action does the actual work (wipes this league's scores for the show,
// resets league_shows.status back to 'live' and winner_sent to null so the
// corrected winner re-announces); this is just the frontend wiring that was
// missing — the action itself has been authenticated and deployed since
// Stage C1.
export async function reopenShow(showId, btn){
  if (!confirm("Reopen this show? This league's scores for it are wiped and it goes back to live so scoring can run again — use this after correcting the setlist on The Carton, then Finalize once it's right.")) return;
  btn.disabled = true; btn.textContent = "…";
  try{
    await edgeFn("reopen", { p_name:state.session.name, p_pin:state.session.pin, league_id:state.currentLeagueId, show_id:showId });
    toast("Show reopened", "score");
    renderAdmin();
  }catch(e){ toast(esc(e.message)); btn.disabled = false; btn.textContent = "Reopen"; }
}
export async function runEdge(action, btn){
  btn.disabled = true; const old = btn.textContent; btn.textContent = "…";
  try{ const r = await edgeFn(action); toast(esc(JSON.stringify(r).slice(0,120)), "score"); if(action==="sync_songs") loadSongs(); }
  catch(e){ toast(esc(e.message)); }
  btn.disabled = false; btn.textContent = old;
}
